// BudgetCards.js
import React, { useEffect, useMemo, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiFood,
  mdiTrainCar,
  mdiWifi,
  mdiDiceMultiple,
  mdiCashMultiple,
  mdiArrowRight,
} from "@mdi/js";
import { Link } from "react-router-dom";
import "./BudgetCards.css";

const API_BASE_URL = process.env.REACT_APP_EXPRESS_API;
// Pastikan endpoint ini mengarah ke fungsi getComparison
const TX_API = `${API_BASE_URL}/api/statistics/card`;

// Urutan tampilan kartu di UI
const UI_LABELS = [
  "Makanan & Kebutuhan Rumah Tangga",
  "Transportasi",
  "Konektivitas",
  "Hiburan",
  "Tagihan & Lainnya",
];

// Mapping dari Key Backend (snake_case atau raw) ke Label UI yang Cantik
const KEY_TO_LABEL = {
  makanan_kebutuhan_rumah_tangga: "Makanan & Kebutuhan Rumah Tangga",
  transportasi: "Transportasi",
  konektivitas: "Konektivitas",
  hiburan: "Hiburan",
  tagihan_lainnya: "Tagihan & Lainnya",
  // Backup jika backend mengirim key asli
  "Makanan & Kebutuhan Rumah Tangga": "Makanan & Kebutuhan Rumah Tangga",
  Transportasi: "Transportasi",
  Konektivitas: "Konektivitas",
  Hiburan: "Hiburan",
  "Tagihan & Lainnya": "Tagihan & Lainnya",
};

const CATEGORY_ICONS = {
  "Makanan & Kebutuhan Rumah Tangga": mdiFood,
  Transportasi: mdiTrainCar,
  Konektivitas: mdiWifi,
  Hiburan: mdiDiceMultiple,
  "Tagihan & Lainnya": mdiCashMultiple,
};

const CATEGORY_COLORS = {
  blue: "#1E40AF",   // Aman
  orange: "#FBBF24", // Belum ada data / Budget 0
  red: "#EF4444",    // Overbudget
};

const OVER_BG = "#FEE2E2";
const OVER_BORDER = "#EF4444";
const OVER_TEXT = "#B91C1C";

// Helper format uang
const currency = (n) =>
  `Rp${Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;

// Helper format tanggal
const fmtDate = (d, withYear = true) =>
  new Date(d).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    ...(withYear ? { year: "numeric" } : {}),
  });

// Helper label rentang tanggal
const rangeLabel = (startISO, endISO) => {
  if (!startISO || !endISO) return "-";
  const s = new Date(startISO);
  const e = new Date(endISO);
  const sameYear = s.getFullYear() === e.getFullYear();
  const left = fmtDate(s, !sameYear);
  const right = fmtDate(e, true);
  return `${left} – ${right}`;
};

export default function BudgetCards() {
  // State sekarang cukup menyimpan summary dari API
  const [summaryData, setSummaryData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(TX_API, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const raw = await res.json();

        if (!res.ok) throw new Error(raw?.message || "Gagal memuat data anggaran");

        // Simpan respons API langsung
        setSummaryData(raw);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Terjadi kesalahan koneksi");
      }
    })();
  }, []);

  // Membangun data kartu berdasarkan UI_LABELS agar urutan tetap
  const cards = useMemo(() => {
    // Jika data belum ada, return array kosong
    if (!summaryData || !summaryData.comparison) return [];

    return UI_LABELS.map((label) => {
      // 1. Cari data dari API yang cocok dengan Label UI saat ini
      // Kita cek apakah KEY_TO_LABEL[api_category] sama dengan label UI kita
      const dataItem = summaryData.comparison.find(item => {
        const normalizedLabel = KEY_TO_LABEL[item.category] || item.category;
        return normalizedLabel === label;
      });

      // 2. Ambil nilai (fallback ke 0 jika tidak ketemu)
      const spent = dataItem ? dataItem.expenditure : 0;
      const budget = dataItem ? dataItem.budget : 0;
      const percentUsage = dataItem ? dataItem.percent_usage : 0;

      // 3. Tentukan logika warna
      const isOver = budget > 0 ? spent > budget : false;
      const hasData = spent > 0 || budget > 0;

      let colorType = "blue";
      if (!hasData) colorType = "orange";
      else if (isOver) colorType = "red";

      const color = CATEGORY_COLORS[colorType];
      // Format persen (bulatkan)
      const percentDisplay = !isOver && budget > 0 ? Math.round(percentUsage) : null;

      return {
        label,
        spent,
        budget,
        isOver,
        percent: percentDisplay,
        colorType,
        color,
        icon: CATEGORY_ICONS[label] || mdiCashMultiple,
      };
    });
  }, [summaryData]);

  if (error) return <div className="budget-error">{error}</div>;

  // Ambil tanggal dari root object summary
  const shownStart = summaryData?.startDate;
  const shownEnd = summaryData?.endDate;
  const periodName = summaryData?.period_name;

  return (
    <div className="budget-cards-container">
      {/* Header: Judul + Periode */}
      <div style={{ textAlign: "left", marginBottom: 10 }}>
        <h3 style={{ margin: 0, color: "#000000", fontWeight: 800, fontSize: 18 }}>
          Perbandingan Pengeluaran / Anggaran {periodName}
        </h3>
        <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
          Periode:{" "}
          <b style={{ color: "#111827" }}>
            {rangeLabel(shownStart, shownEnd)}
          </b>
        </div>
      </div>

      {/* Grid Kartu */}
      <div className="budget-cards-grid">
        {cards.map((item) => {
          const border = item.colorType === "red" ? OVER_BORDER : item.color;
          const bg =
            item.colorType === "red"
              ? OVER_BG
              : item.colorType === "orange"
                ? "#FEF9C3"
                : "#fff";
          const text = item.colorType === "red" ? OVER_TEXT : item.color;

          return (
            <div
              key={item.label}
              className="budget-card"
              style={{ borderColor: border, backgroundColor: bg, size: 200 }}
            >
              <div className="card-tip right">
                <div className="tip-badge" style={{ backgroundColor: text }}>i</div>
                <div className="tip-pop" style={{ borderColor: border }}>
                  <div className="tip-line">
                    {currency(item.spent)} <span className="tip-sep">/</span> {currency(item.budget)}
                  </div>
                  {!item.isOver && item.percent !== null && (
                    <div className="tip-sub">({item.percent}%)</div>
                  )}
                  {item.isOver && <div className="tip-sub over">Melebihi anggaran.</div>}
                  <div className="tip-explain">
                    🔵 <b>Biru:</b> Dalam batas anggaran<br />
                    🟡 <b>Kuning:</b> Belum ada data<br />
                    🔴 <b>Merah:</b> Melebihi anggaran
                  </div>
                </div>
              </div>

              <div className="amount-block" style={{ color: text }}>
                <div className="amount-top">{currency(item.spent)}</div>
                <div className="amount-sub">
                  <span className="slash">/</span> {currency(item.budget)}
                  {!item.isOver && item.percent !== null && (
                    <span className="percent"> ({item.percent}%)</span>
                  )}
                </div>
              </div>

              <div className="budget-card-bottom">
                <div className="budget-card-icon">
                  <Icon path={item.icon} size={2.2} color={text} />
                </div>
                <div className="budget-card-category" style={{ color: text }}>
                  {item.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="budget-footer">
        <Link className="budget-see-more-btn" to="/anggaran">
          Set Anggaran <Icon path={mdiArrowRight} size={0.65} />
        </Link>
      </div>
    </div>
  );
}